import React, { useState } from 'react';
import './ScheduleCalendar.css';

const ScheduleCalendar = ({ 
  classes, 
  templates, 
  onClassClick, 
  onDateClick 
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDayClasses, setSelectedDayClasses] = useState([]);
  const [selectedModalDate, setSelectedModalDate] = useState(null);

  const parseLocalDateTime = (dateTimeString) => {
    if (!dateTimeString) return null;
    
    if (typeof dateTimeString === 'string' && dateTimeString.includes(' ')) {
      const [datePart, timePart] = dateTimeString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
      
      return new Date(year, month - 1, day, hour, minute, second || 0);
    }
    
    if (dateTimeString.includes('T') && dateTimeString.includes('Z')) {
      const utcDate = new Date(dateTimeString);
      return new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate(),
        utcDate.getUTCHours(),
        utcDate.getUTCMinutes(),
        utcDate.getUTCSeconds()
      );
    }
    
    if (dateTimeString.includes('T')) {
      return new Date(dateTimeString);
    }
    
    if (dateTimeString.includes('-') && !dateTimeString.includes(' ')) {
      const [year, month, day] = dateTimeString.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    return new Date(dateTimeString);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const startCalendar = new Date(firstDay);
    
    startCalendar.setDate(startCalendar.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startCalendar);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const getClassesForDate = (date) => {
    const targetYear = date.getFullYear();
    const targetMonth = date.getMonth() + 1;
    const targetDay = date.getDate();
    
    return classes.filter(classItem => {
      const classDateTime = parseLocalDateTime(classItem.start_time);
      if (!classDateTime) return false;
      
      const classYear = classDateTime.getFullYear();
      const classMonth = classDateTime.getMonth() + 1;
      const classDay = classDateTime.getDate();
      
      return (classYear === targetYear && classMonth === targetMonth && classDay === targetDay);
    }).sort((a, b) => {
      const timeA = parseLocalDateTime(a.start_time);
      const timeB = parseLocalDateTime(b.start_time);
      return timeA - timeB;
    });
  };

  const formatTime = (dateTimeString) => {
    const dateTime = parseLocalDateTime(dateTimeString);
    if (!dateTime) return '00:00';
    
    const hours = dateTime.getHours();
    const minutes = dateTime.getMinutes();
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const handleDayClick = (date) => {
    const dayClasses = getClassesForDate(date);
    
    if (dayClasses.length > 0) {
      setSelectedDayClasses(dayClasses);
      setSelectedModalDate(date);
      setShowDayModal(true);
    } else {
      if (onDateClick) {
        onDateClick(date);
      }
    }
  };

  const days = getDaysInMonth(currentDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const isToday = (date) => {
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate.getTime() === today.getTime();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const isPastDate = (date) => {
    const compareDate = new Date(date);
    compareDate.setHours(23, 59, 59, 999);
    return compareDate < today;
  };

  const getClassColor = (classItem) => {
    const template = templates.find(t => 
      t.id === classItem.template_id || 
      t.class_name === classItem.class_name
    );
    
    return template?.color || '#FF6B35';
  };

  const formatModalDate = (date) => {
    return date.toLocaleDateString('ru-RU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const calculateDuration = (startTime, endTime) => {
    const start = parseLocalDateTime(startTime);
    const end = parseLocalDateTime(endTime);
    
    if (!start || !end) return '';
    
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 60) {
      return `${diffMins}м`;
    }
    
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;
    
    return minutes > 0 ? `${hours}ч ${minutes}м` : `${hours}ч`;
  };

  return (
    <div className="schedule-calendar">
      {/* Header */}
      <div className="schedule-calendar-header">
        <button 
          className="schedule-calendar-nav"
          onClick={() => navigateMonth(-1)}
          title="Предыдущий месяц"
        >
          ‹
        </button>
        <h3 className="schedule-calendar-title">
          {currentDate.toLocaleDateString('ru-RU', { 
            month: 'long', 
            year: 'numeric' 
          })}
        </h3>
        <button 
          className="schedule-calendar-nav"
          onClick={() => navigateMonth(1)}
          title="Следующий месяц"
        >
          ›
        </button>
      </div>

      {/* Weekdays */}
      <div className="schedule-calendar-weekdays">
        {['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'].map(day => (
          <div key={day} className="schedule-calendar-weekday">
            {day}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="schedule-calendar-grid">
        {days.map((day, index) => {
          const dayClasses = getClassesForDate(day);
          
          return (
            <div
              key={index}
              className={`schedule-calendar-day ${
                !isCurrentMonth(day) ? 'other-month' : ''
              } ${
                isToday(day) ? 'today' : ''
              } ${
                isPastDate(day) ? 'past' : ''
              } ${
                dayClasses.length > 0 ? 'has-classes' : ''
              }`}
              onClick={() => handleDayClick(day)}
            >
              <div className="schedule-day-number">
                {day.getDate()}
              </div>
              
              {dayClasses.length > 0 && (
                <div className="schedule-day-dots">
                  {dayClasses.slice(0, 3).map((classItem) => (
                    <div
                      key={classItem.id}
                      className="schedule-class-dot"
                      style={{ backgroundColor: getClassColor(classItem) }}
                      title={`${classItem.class_name} - ${formatTime(classItem.start_time)}`}
                    />
                  ))}
                  {dayClasses.length > 3 && (
                    <span className="schedule-more-dots">+{dayClasses.length - 3}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Stats */}
      <div className="schedule-calendar-stats">
        <span className="schedule-stats-label">Занятий в месяце</span>
        <span className="schedule-stats-value">
          {classes.filter(classItem => {
            const classDate = parseLocalDateTime(classItem.start_time);
            return classDate && classDate.getMonth() === currentDate.getMonth() && 
                   classDate.getFullYear() === currentDate.getFullYear();
          }).length}
        </span>
      </div>

      {/* Day Classes Modal */}
      {showDayModal && selectedModalDate && (
        <div className="schedule-modal-overlay" onClick={() => setShowDayModal(false)}>
          <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="schedule-modal-header">
              <h2>{formatModalDate(selectedModalDate)}</h2>
              <button 
                className="schedule-modal-close"
                onClick={() => setShowDayModal(false)}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="schedule-modal-body">
              <div className="schedule-modal-count">
                {selectedDayClasses.length} {
                  selectedDayClasses.length === 1 ? 'занятие' : 
                  selectedDayClasses.length < 5 ? 'занятия' : 'занятий'
                }
              </div>

              <div className="schedule-modal-list">
                {selectedDayClasses.map(classItem => (
                  <div 
                    key={classItem.id}
                    className="schedule-modal-class"
                    onClick={() => {
                      if (onClassClick) {
                        onClassClick(classItem);
                      }
                      setShowDayModal(false);
                    }}
                  >
                    <div className="schedule-modal-class-time">
                      <span className="time-start">{formatTime(classItem.start_time)}</span>
                      <span className="time-sep">—</span>
                      <span className="time-end">{formatTime(classItem.end_time)}</span>
                      <span className="time-duration">
                        ({calculateDuration(classItem.start_time, classItem.end_time)})
                      </span>
                    </div>

                    <h4 className="schedule-modal-class-name">{classItem.class_name}</h4>

                    {classItem.trainer && (
                      <p className="schedule-modal-class-trainer">
                        Тренер: {classItem.trainer}
                      </p>
                    )}

                    {classItem.category && (
                      <span className="schedule-modal-class-badge">{classItem.category}</span>
                    )}

                    <div className="schedule-modal-class-meta">
                      <span className="meta-capacity">
                        {classItem.booked || 0} / {classItem.capacity} мест
                      </span>
                      <span className="meta-price">
                        {parseFloat(classItem.price).toFixed(0)} ₽
                      </span>
                    </div>

                    <div 
                      className="schedule-modal-class-indicator"
                      style={{ backgroundColor: getClassColor(classItem) }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="schedule-modal-footer">
              <button 
                className="schedule-btn schedule-btn-secondary"
                onClick={() => {
                  if (onDateClick) {
                    onDateClick(selectedModalDate);
                  }
                  setShowDayModal(false);
                }}
              >
                Перейти к списку
              </button>
              <button 
                className="schedule-btn schedule-btn-primary"
                onClick={() => setShowDayModal(false)}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleCalendar;
